import { Injectable } from '@angular/core';
import { IAnalyticsService, EventProperties, UserProperties } from './analytics.interface';
import { PosthogAnalyticsService } from './posthog-analytics.service';
import { GtmAnalyticsService } from './gtm-analytics.service';

@Injectable({
  providedIn: 'root'
})
export class AnalyticsService implements IAnalyticsService {
  private providers: IAnalyticsService[] = [];
  private isEnabled: boolean = true;

  constructor(
    private posthogService: PosthogAnalyticsService,
    private gtmService: GtmAnalyticsService
  ) {
    this.init();
  }

  init(): void {
    // Initialize all providers
    this.providers = [
      this.posthogService,
      this.gtmService
    ];

    console.log('Unified Analytics Service initialized with PostHog and GTM');
  }

  /**
   * Enable or disable analytics tracking
   */
  setEnabled(enabled: boolean): void {
    this.isEnabled = enabled;
    console.log(`Analytics ${enabled ? 'enabled' : 'disabled'}`);
  }

  /**
   * Track custom events to all providers
   */
  trackEvent(eventName: string, properties?: EventProperties): void {
    if (!this.isEnabled) {
      console.debug(`Analytics disabled. Event not tracked: ${eventName}`);
      return;
    }

    // Send event to all providers
    this.providers.forEach(provider => {
      try {
        provider.trackEvent(eventName, properties);
        console.debug(`Event tracked to ${provider.constructor.name}: ${eventName}`, properties);
      } catch (error) {
        console.error(`Failed to track event to ${provider.constructor.name}:`, error);
      }
    });
  }

  /**
   * Track page views to all providers
   */
  trackPageView(pageName: string, properties?: EventProperties): void {
    if (!this.isEnabled) {
      console.debug(`Analytics disabled. Page view not tracked: ${pageName}`);
      return;
    }

    // Send page view to all providers
    this.providers.forEach(provider => {
      try {
        provider.trackPageView(pageName, properties);
        console.debug(`Page view tracked to ${provider.constructor.name}: ${pageName}`, properties);
      } catch (error) {
        console.error(`Failed to track page view to ${provider.constructor.name}:`, error);
      }
    });
  }

  /**
   * Identify user to all providers
   */
  identify(userId: string, properties?: UserProperties): void {
    if (!this.isEnabled) {
      console.debug(`Analytics disabled. User not identified: ${userId}`);
      return;
    }

    // Send identify to all providers
    this.providers.forEach(provider => {
      try {
        provider.identify(userId, properties);
        console.debug(`User identified in ${provider.constructor.name}: ${userId}`, properties);
      } catch (error) {
        console.error(`Failed to identify user in ${provider.constructor.name}:`, error);
      }
    });
  }

  /**
   * Set user properties to all providers
   */
  setUserProperties(properties: UserProperties): void {
    if (!this.isEnabled) {
      console.debug('Analytics disabled. User properties not set');
      return;
    }

    // Send user properties to all providers
    this.providers.forEach(provider => {
      try {
        provider.setUserProperties(properties);
        console.debug(`User properties set in ${provider.constructor.name}:`, properties);
      } catch (error) {
        console.error(`Failed to set user properties in ${provider.constructor.name}:`, error);
      }
    });
  }

  /**
   * Reset user data in all providers
   */
  reset(): void {
    // Reset all providers
    this.providers.forEach(provider => {
      try {
        provider.reset();
        console.debug(`Reset completed in ${provider.constructor.name}`);
      } catch (error) {
        console.error(`Failed to reset ${provider.constructor.name}:`, error);
      }
    });
  }

  /**
   * Helper method to track common e-commerce events
   */
  trackPurchase(orderId: string, amount: number, currency: string, items?: any[]): void {
    this.trackEvent('purchase', {
      order_id: orderId,
      value: amount,
      currency: currency,
      items: items
    });
  }

  /**
   * Helper method to track form submissions
   */
  trackFormSubmit(formName: string, formData?: any): void {
    this.trackEvent('form_submit', {
      form_name: formName,
      form_data: formData
    });
  }

  /**
   * Helper method to track button clicks
   */
  trackButtonClick(buttonName: string, metadata?: any): void {
    this.trackEvent('button_click', {
      button_name: buttonName,
      ...metadata
    });
  }

  /**
   * Helper method to track search events
   */
  trackSearch(searchQuery: string, resultsCount?: number): void {
    this.trackEvent('search', {
      search_term: searchQuery,
      results_count: resultsCount
    });
  }

  /**
   * Helper method to track social sharing
   */
  trackShare(method: string, contentId?: string, contentType?: string): void {
    this.trackEvent('share', {
      method: method,
      content_id: contentId,
      content_type: contentType
    });
  }

  /**
   * Helper method to track errors
   */
  trackError(errorMessage: string, errorStack?: string, metadata?: any): void {
    this.trackEvent('error', {
      error_message: errorMessage,
      error_stack: errorStack,
      ...metadata
    });
  }
}